import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./styles/globals.css";
import "./styles/animations.css";
import "driver.js/dist/driver.css";

import ErrorBoundary from "./components/UI/ErrorBoundary";

// Loading component while waiting for PyWebview
const LoadingScreen = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center text-white">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-6"></div>
      <h2 className="text-xl font-semibold mb-2">Initializing Radspeak AI</h2>
      <p className="text-white/70">
        Setting up audio system and AI providers...
      </p>
    </div>
  </div>
);

// API initialization and testing
class APIManager {
  constructor() {
    this.isReady = false;
    this.api = null;
  }

  async waitForPyWebview() {
    return new Promise((resolve) => {
      if (window.pywebview) {
        resolve();
      } else {
        window.addEventListener("pywebviewready", resolve, { once: true });
      }
    });
  }

  async initialize() {
    try {
      // Wait for pywebview to be ready
      await this.waitForPyWebview();

      // Wait for API to be available
      await window.pywebview.api;
      this.api = window.pywebview.api;

      // Test basic API connectivity
      await this.testAPI();

      this.isReady = true;
      return true;
    } catch (error) {
      console.error("API initialization failed:", error);
      throw error;
    }
  }

  async testAPI() {
    try {
      // Test basic API call
      if (typeof this.api.ping === "function") {
        const result = await this.api.ping();
      }

      // Test audio system availability
      if (typeof this.api.get_audio_devices === "function") {
        const audioDevices = await this.api.get_audio_devices();
      }

      // Test transcription providers
      if (typeof this.api.get_providers === "function") {
        const providers = await this.api.get_providers();
      }
    } catch (error) {
      console.warn("Some API functions not available during test:", error);
      // Don't throw - some functions might not be implemented yet
    }
  }

  // Make API globally available
  makeGlobalAPI() {
    window.radspeakai_api = this.api;
  }
}

// Main initialization function
async function initializeApp() {
  const root = ReactDOM.createRoot(document.getElementById("root"));

  // Show loading screen
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <LoadingScreen />
      </ErrorBoundary>
    </React.StrictMode>
  );

  try {
    // Initialize API
    const apiManager = new APIManager();
    await apiManager.initialize();

    // Make API globally available for other components
    apiManager.makeGlobalAPI();

    // API is ready, render the main app
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </React.StrictMode>
    );
  } catch (error) {
    console.error("💥 Failed to initialize application:", error);

    // Render error state with retry option
    root.render(
      <React.StrictMode>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center text-white p-8">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-4">Initialization Failed</h1>
            <p className="text-white/70 mb-4">
              Failed to connect to the Radspeak AI backend.
            </p>
            <p className="text-sm text-white/50 mb-6">{error.message}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="bg-purple-600 hover:bg-purple-700 px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Retry
              </button>
              <details className="text-left bg-black/20 rounded-lg p-4">
                <summary className="cursor-pointer text-sm text-white/60 mb-2">
                  Technical Details
                </summary>
                <pre className="text-xs text-red-300 whitespace-pre-wrap">
                  {error.stack}
                </pre>
              </details>
            </div>
          </div>
        </div>
      </React.StrictMode>
    );
  }
}

// Error handling for unhandled promises
window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled promise rejection:", event.reason);
  // Optionally show user notification
});

// Error handling for uncaught exceptions
window.addEventListener("error", (event) => {
  console.error("Uncaught error:", event.error);
  // Optionally show user notification
});

// Start the application
initializeApp();
